import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../api/client';

/**
 * Role-based identity for the internal app (Task #162).
 *
 * The single source of truth for "who is signed in" is the backend session
 * cookie (`spartis_site_session`), surfaced by `GET /site-users/me`. This module
 * fetches that identity and maps a staff role to the set of navigation panels it
 * may see, so the sidebar narrows to a staff member's relevant tools.
 *
 * SECURITY NOTE: this is UX gating only. The authoritative access control lives
 * in the backend (`require_admin` + initiative/site quarantine). A preview-only
 * visitor still sees the UI shell, but every protected API call fails server-side
 * — they get "no staff/admin access" regardless of what the nav shows.
 */

export interface AuthAssignment {
    project_config_id: number;
    site_id: string | null;
}

export interface AuthIdentity {
    user_id: string;
    site_id: string | null;
    role: string;
    is_staff: boolean;
    is_read_only: boolean;
    assignments: AuthAssignment[];
}

/** Dispatched after sign-in / sign-out so the identity hook re-fetches `/me`. */
export const IDENTITY_CHANGED_EVENT = 'spartis:identityChanged';

export function notifyIdentityChanged(): void {
    try {
        window.dispatchEvent(new CustomEvent(IDENTITY_CHANGED_EVENT));
    } catch {
        /* no-op in non-browser/test environments */
    }
}

/**
 * Per-role allow-lists of panel indices (see replit.md §3 "Nav panel index").
 * Indices are stable. `internal_admin` is intentionally absent → it falls
 * through to "see everything". Roles without an entry (e.g. a site worker that
 * somehow reaches the internal shell) get an empty list.
 */
export const ROLE_PANELS: Record<string, number[]> = {
    // evaluator — measurement / evaluation surfaces (dashboards, simulation,
    // CARI, CFIR/ERIC, record review, governance, audit, cost capture, measure
    // hub, CEDAR/LEAF + research review, health, integration health, AI usage &
    // governance). Cost Capture (21) is the evaluator's cost/process-capture
    // surface (A2 gap fix); AI Usage & Governance (27) is its read-only view of
    // where AI touches the platform; Cost Ledger (28) is the evaluator's
    // row-level stage-aware implementation cost ledger (Task #369); Scenario
    // Library (29) is the evaluator's Phase I simulated-testing scenario
    // definition library (Task #436); Testing Feedback (30) is the Phase I
    // testing-feedback review dashboard (#435); Peer Exchange (31) surfaces
    // the closeout review queue + candidate-signal governance.
    // Source Capture (32) is the staff manual meeting-source capture surface
    // (Task #530) — paste/upload transcripts + notes, retention choice,
    // review-gated draft signals.
    // CEDAR LEAF (23) + Research Review (24) were REMOVED (2026-07-29):
    // corpus governance is Synterra-Admin-only — "Evaluator / Reviewer" reviews
    // implementation/testing outputs, not the knowledge corpus. Evaluators
    // request evidence additions via Suggest a Source (34) instead.
    evaluator: [0, 1, 2, 3, 4, 5, 8, 16, 18, 19, 20, 21, 22, 26, 27, 28, 29, 30, 31, 32, 34],
    // ta_provider — TA delivery surfaces (dashboards, CARI, both chats, TA
    // review queue, CFIR/ERIC, toolkit, site profiles, verticals, peer
    // exchange (31) — the L2 pathway TA providers moderate + confirm.
    // Cost Ledger (28) — TA providers ENTER event-level costs near the work
    // (provenance auto-stamped ta_provider_form); review/confirm/delete stay
    // evaluator/admin-only, enforced by the backend reviewer guard.
    // Suggest a Source (34) — the non-admin pathway to request CEDAR corpus
    // additions (corpus governance itself is admin-only).
    ta_provider: [0, 1, 2, 4, 5, 6, 7, 8, 9, 12, 14, 28, 31, 32, 34],
    // read_only_partner — view-only: dashboards, CFIR/ERIC, audit, AI usage &
    // governance (27, itself a read-only surface). The backend additionally
    // blocks any write for this role.
    read_only_partner: [0, 1, 2, 8, 18, 27],
};

/** Roles that are internal staff (mirrors backend STAFF_ROLES). */
const STAFF_ROLES = new Set(['internal_admin', 'evaluator', 'ta_provider', 'read_only_partner']);

/** Friendly, human-readable labels for staff roles (shared across the UI). */
export const ROLE_LABELS: Record<string, string> = {
    internal_admin: 'Synterra Admin',
    evaluator: 'Evaluator / Reviewer',
    ta_provider: 'Implementation Support Provider',
    read_only_partner: 'Read-only Partner',
};

/**
 * Resolve a friendly label for the lens the viewer is currently operating in.
 *
 * Priority: a signed-in staff identity wins (most specific), then the
 * break-glass admin code, then pitch/demo mode, then a non-staff site session,
 * then the anonymous research-preview shell. This is display-only — it never
 * grants access (the backend remains the boundary).
 */
export function viewerRoleLabel(
    identity: AuthIdentity | null,
    opts: { hasAdminCode: boolean; pitchMode: boolean },
): string {
    if (identity && identity.is_staff && STAFF_ROLES.has(identity.role)) {
        const base = ROLE_LABELS[identity.role] || identity.role;
        return identity.is_read_only ? `${base} (view-only)` : base;
    }
    if (opts.hasAdminCode) return 'Admin (break-glass)';
    if (opts.pitchMode) return 'Demo Mode';
    if (identity && !identity.is_staff) return 'Site Representative';
    return 'Research Preview';
}

/**
 * Public research-preview shell: the analytic / demo surfaces a preview-only
 * (no staff session, no admin code) visitor may see. Deliberately EXCLUDES the
 * staff/admin operational panels — Initiative Setup (10), Project Config (11),
 * Site Profiles (12), Site Staff (13), Verticals (14), Bulk Upload (15),
 * System Health (16), Digest & Audit (18), Governance (19), Record Review (20),
 * Cost Capture (21), Measure Hub (22), and the admin TA Review Queue (7). Those
 * are all backend admin-guarded; hiding them from the preview nav keeps
 * "preview unlocks the shell only" true at the UX layer too (the backend is
 * still the real boundary). Verticals (14) is an operations surface — staff
 * roles only, never the public/site shell (2026-07-14).
 * Keeps Issue Tracker (17) — it is platform-noise gated by pitch mode, not an
 * admin surface, and the research preview surfaces it when pitch mode is off.
 */
export const PUBLIC_PANELS: number[] = [0, 1, 2, 3, 4, 5, 6, 8, 9, 17];

/**
 * Resolve the set of panel indices a viewer may see.
 *
 * - Break-glass admin code holder → all panels (matches today's admin UX).
 * - Pitch/demo mode → all panels (the curated NIH demo is not role-gated).
 * - Signed-in staff → that role's allow-list (`internal_admin` ⇒ all).
 * - Everyone else (preview-only / anonymous, and any non-staff session) →
 *   the restricted {@link PUBLIC_PANELS} shell: demo/analytic surfaces only,
 *   never the staff/admin operational panels. The backend still denies the
 *   underlying data, so this is defense-in-depth, not the sole boundary.
 *
 * Returns `null` to mean "no restriction" (show every panel).
 */
export function allowedPanels(
    identity: AuthIdentity | null,
    opts: { hasAdminCode: boolean; pitchMode: boolean },
): Set<number> | null {
    if (opts.hasAdminCode || opts.pitchMode) return null;
    if (identity && identity.is_staff && STAFF_ROLES.has(identity.role)) {
        if (identity.role === 'internal_admin') return null;
        return new Set(ROLE_PANELS[identity.role] ?? []);
    }
    return new Set(PUBLIC_PANELS);
}

/**
 * React hook: fetch the current identity from `/site-users/me`, re-fetching on
 * sign-in/out (`IDENTITY_CHANGED_EVENT`) and cross-tab `storage` changes. Never
 * throws — a 401 / network error resolves to `identity = null`.
 */
export function useAuthIdentity() {
    const [identity, setIdentity] = useState<AuthIdentity | null>(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            // Migrated to the shared API client (Synterra A3): 'public' context —
            // NOT 'site' — because this who-am-I probe legitimately 401s for fresh
            // visitors and must never fire the site session-expired handler.
            const resp = await apiFetch('/site-users/me', { context: 'public' });
            if (resp.ok) {
                const d = await resp.json();
                setIdentity({
                    user_id: d.user_id,
                    site_id: d.site_id ?? null,
                    role: d.role,
                    is_staff: !!d.is_staff,
                    is_read_only: !!d.is_read_only,
                    assignments: Array.isArray(d.assignments) ? d.assignments : [],
                });
            } else {
                setIdentity(null);
            }
        } catch {
            setIdentity(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
        const onChange = () => { refresh(); };
        window.addEventListener(IDENTITY_CHANGED_EVENT, onChange);
        window.addEventListener('storage', onChange);
        return () => {
            window.removeEventListener(IDENTITY_CHANGED_EVENT, onChange);
            window.removeEventListener('storage', onChange);
        };
    }, [refresh]);

    return { identity, loading, refresh };
}
